import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Play, 
  RotateCcw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Award,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import CodeEditor from '../components/CodeEditor/CodeEditor';
import projectService from '../services/projectService';
import { useAuthStore } from '../store/useAuthStore';

const ProjectView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [project, setProject] = useState(null);
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [revealedHints, setRevealedHints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProject(projectId);
      setProject(data.project);
      setCode(data.project.starter_code || '# Write your code here\n');
      
      if (user) {
        loadSubmissions();
      }
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const subs = await projectService.getSubmissions(projectId);
      setSubmissions(subs);
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please login to submit projects');
      navigate('/login');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmissionResult(null);
      
      const result = await projectService.submitProject(projectId, code);
      setSubmissionResult(result);
      
      if (result.passed) {
        toast.success('🎉 Congratulations! Project passed!');
      } else {
        toast.error('Some tests failed. Review the results below.');
      }
      
      // Reload submissions
      loadSubmissions();
    } catch (error) {
      console.error('Error submitting project:', error);
      toast.error(error.response?.data?.message || 'Failed to submit project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset your code?')) {
      setCode(project.starter_code || '# Write your code here\n');
      setSubmissionResult(null);
      toast.success('Code reset to starter template');
    }
  };

  const revealHint = (index) => {
    if (!revealedHints.includes(index)) {
      setRevealedHints([...revealedHints, index]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-xl text-gray-700">Project not found</p>
        </div>
      </div>
    );
  }

  const bestScore = submissions.length > 0 
    ? Math.max(...submissions.map(s => s.score)) 
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  project.difficulty_level === 'beginner' ? 'bg-green-100 text-green-800' :
                  project.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {project.difficulty_level}
                </span>
                <span className="flex items-center text-sm text-gray-500">
                  <Clock className="h-4 w-4 mr-1" />
                  {project.estimated_duration_hours} hours
                </span>
                {bestScore > 0 && (
                  <span className="flex items-center text-sm text-primary-500 font-medium">
                    <Award className="h-4 w-4 mr-1" />
                    Best: {bestScore}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Project Info */}
          <div className="space-y-6">
            {/* Description */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Project Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{project.description}</p>
            </div>

            {/* Objectives */}
            {project.objectives && project.objectives.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Learning Objectives</h2>
                <ul className="space-y-2">
                  {project.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Requirements */}
            {project.requirements && project.requirements.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Requirements</h2>
                <ul className="space-y-2">
                  {project.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary-500 font-bold mr-2">{index + 1}.</span>
                      <span className="text-gray-700">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Test Cases */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Test Cases</h2>
              <div className="space-y-3">
                {project.test_cases && project.test_cases.map((testCase, index) => (
                  <div key={testCase.test_id} className="border-l-4 border-primary-500 pl-4 py-2">
                    <p className="font-medium text-gray-900">Test {index + 1}: {testCase.description}</p>
                    {!testCase.is_hidden && (
                      <>
                        {testCase.input && (
                          <p className="text-sm text-gray-600 mt-1">
                            <span className="font-medium">Input:</span> {testCase.input}
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Expected Output:</span> {testCase.expected_output}
                        </p>
                      </>
                    )}
                    {testCase.is_hidden && (
                      <p className="text-sm text-gray-500 italic mt-1">
                        <EyeOff className="inline h-4 w-4 mr-1" />
                        Hidden test case
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Points: {testCase.points || 1}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hints */}
            {project.hints && project.hints.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <button
                  onClick={() => setShowHints(!showHints)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-semibold">Hints</h2>
                  {showHints ? <ChevronUp /> : <ChevronDown />}
                </button>
                {showHints && (
                  <div className="mt-4 space-y-3">
                    {project.hints.sort((a, b) => a.order - b.order).map((hint, index) => (
                      <div key={hint.hint_id} className="border rounded-lg p-4">
                        {revealedHints.includes(index) ? (
                          <p className="text-gray-700">{hint.content}</p>
                        ) : (
                          <button
                            onClick={() => revealHint(index)}
                            className="text-primary-500 hover:text-primary-600 font-medium flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Reveal Hint {index + 1}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Submission History */}
            {user && submissions.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <button
                  onClick={() => setShowSubmissions(!showSubmissions)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-semibold">
                    Submission History ({submissions.length})
                  </h2>
                  {showSubmissions ? <ChevronUp /> : <ChevronDown />}
                </button>
                {showSubmissions && (
                  <div className="mt-4 space-y-2">
                    {submissions.map((sub, index) => (
                      <div 
                        key={sub.submission_id} 
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <div>
                          <span className="font-medium">
                            Attempt {submissions.length - index}
                          </span>
                          <span className="text-sm text-gray-500 ml-3">
                            {new Date(sub.submitted_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`font-bold ${
                            sub.passed ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {sub.score}%
                          </span>
                          {sub.passed ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Code Editor */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
                <h2 className="text-white font-semibold">Your Solution</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleReset}
                    className="flex items-center px-3 py-1.5 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors text-sm"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Reset
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center px-4 py-1.5 bg-primary-500 text-black rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-1" />
                        Submit Project
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              <CodeEditor
                value={code}
                onChange={setCode}
                language="python"
                height="500px"
              />
            </div>

            {/* Submission Results */}
            {submissionResult && (
              <div className={`rounded-lg shadow-md p-6 ${
                submissionResult.passed ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center">
                    {submissionResult.passed ? (
                      <>
                        <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                        <span className="text-green-900">Project Passed!</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-6 w-6 text-red-600 mr-2" />
                        <span className="text-red-900">Some Tests Failed</span>
                      </>
                    )}
                  </h3>
                  <div className="text-2xl font-bold">
                    <span className={submissionResult.passed ? 'text-green-600' : 'text-red-600'}>
                      {submissionResult.score}%
                    </span>
                  </div>
                </div>

                <p className={`mb-4 ${submissionResult.passed ? 'text-green-800' : 'text-red-800'}`}>
                  {submissionResult.feedback}
                </p>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Test Results:</h4>
                  {submissionResult.test_results.map((result, index) => (
                    <div 
                      key={result.test_id} 
                      className={`p-4 rounded-lg ${
                        result.passed ? 'bg-white border-l-4 border-green-500' : 'bg-white border-l-4 border-red-500'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Test {index + 1}: {result.description}</span>
                        {result.passed ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      {!result.is_hidden && (
                        <>
                          <div className="text-sm space-y-1">
                            <p className="text-gray-600">
                              <span className="font-medium">Expected:</span> {result.expected_output}
                            </p>
                            <p className={result.passed ? 'text-green-700' : 'text-red-700'}>
                              <span className="font-medium">Got:</span> {result.actual_output}
                            </p>
                          </div>
                        </>
                      )}
                      {result.error && (
                        <p className="text-sm text-red-700 mt-2">
                          <span className="font-medium">Error:</span> {result.error}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4 text-sm text-gray-700">
                  Score: {submissionResult.earned_points} / {submissionResult.total_points} points
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectView;




